/* eslint-disable no-unused-vars */
/* eslint-disable no-await-in-loop,no-plusplus,implicit-arrow-linebreak,no-param-reassign,
no-unused-expressions */
/* global G */
import { asyncPipeSpread } from 'lib/util'
import sequenceComponentFindProxy from 'lib/sequence/component/children/find'

/**
 * Provides the correct `component` to select the quick actions from.
 *
 * `component` can either be a string, in which case we'll look up the correct
 * component using {@link sequenceComponentFindProxy} and return its `G.PROPS`
 * property, or, if it's not a string, it's expected to be the actionUi config.
 * In which case, we just return it.
 *
 * This enables the quick actions hook to work with quick actions not only defined
 * in the action ui, but also in any component config. E.g. the `right` component
 * (`folder`) of the equipment tree.
 *
 * @param {string|object} component
 * @returns {function(*): function(...[*]): *}
 */
const provideComponent = component => obj => (...args) => {
  const actionComponent = obj[G.STATE][G.ACTION][G.COMPONENT]
  const { [component]: target } = sequenceComponentFindProxy(actionComponent)

  return target
    ? [target[G.PROPS], ...args]
    : [component, ...args]
}

/**
 * Shallow copies quick actions in action UI so that altering them doesn't affect the cached
 * configuration.
 *
 * @param {Gaia.AppModule.Spec} obj  the current module composition object
 * @returns {function(...[*]): *[]}
 */
const initialize = obj => (component, ...args) => {
  component.quickActions = component.quickActions?.map(quickAction => ({
    ...quickAction,
    events: {
      ...quickAction.events,
    },
  }))

  return [component, ...args]
}

/**
 * Assigns the configured {@quickAction}s event handlers.
 *
 * @param {Gaia.AppModule.Spec} obj  the current module composition object
 * @returns {function(...[*]): *[]}
 */
const provideEvents = obj => (component, ...args) => {
  const { quickActions = [] } = component

  quickActions.forEach((quickAction) => {
    const events = quickAction && quickAction.events
    quickAction.handlerNames = quickAction.handlerNames || { ...events }
    const moduleEvents = obj[G.EVENTS]
    // hacking redirect event
    quickAction[G.CONFIGURATION] = { route: quickAction.route }
    if (events && moduleEvents) {
      Object.keys(quickAction.handlerNames)
        .forEach((name) => {
          const handlerName = quickAction.handlerNames[name]
          const handlerExists = moduleEvents[name]
            && Object.prototype.hasOwnProperty.call(moduleEvents[name], handlerName)
          // todo: turn into pipe(hasNamespace, usesNamespace)
          handlerExists
            ? events[name] = moduleEvents[name][handlerName](obj, quickAction)
            : console.warn(
              `Missing required "${name}" event handler for quick action`,
              quickAction.key,
            )
        })
    }
  })

  return [component, ...args]
}

/**
 * Requests the current translation of each visible `quickAction`.
 *
 * @param {Gaia.AppModule.Spec} obj  the current module composition object
 * @returns {function(...[*]): *[]}
 */
const translateTitles = obj => (component, ...args) => {
  const { quickActions = [] } = component
  const currentModule = obj[G.CONFIGURATION].module

  quickActions.forEach((quickAction) => {
    quickAction.title = obj[G.ADAPTER][G.INTL]._t(
      `actions.${quickAction.key}`,
      {
        ns: currentModule,
        _key: `actions.${quickAction.key}`,
        defaultValue: quickAction.title,
      },
    )
  })

  return args
}

/**
 * Tree Quick Actions Hook
 *
 * Executes the `quickActions` hook, passing it `component`.
 *
 * @param {string} component name of the component to use the quick actions of
 *
 * @returns {function(*): function(...[*]): Promise<*>}
 */
export const componentQuickActions = component => obj => async (...args) => asyncPipeSpread(
  provideComponent(component)(obj),
  initialize(obj),
  provideEvents(obj),
  translateTitles(obj),
)(...args)

/**
 * QuickActions Hook
 *
 * Configures the quick actions to be displayed in ui appbar segment.
 *
 * @param {Gaia.AppModule.Spec} obj  the current module composition object
 * @returns {function(...[*]): Promise<*[]>}
 */
export default obj => async (...args) => asyncPipeSpread(
  provideComponent(obj[G.STATE][G.ACTION][G.UI])(obj),
  initialize(obj),
  provideEvents(obj),
  translateTitles(obj),
)(...args)
